/**
 * Reports: completion rates per course and site over a date range (plan
 * docs/plans/lms-plan.md 13 tab 6; Appendix A GET
 * /admin/reports/completion-rates; 18.23; R16, R23; WP-45).
 *
 * The population is every assignment due in the range (from's local midnight
 * to the end of to, in the network's time zone) held by people on the roster
 * of the chosen site, or of every site the admin reaches. Per course:
 * assigned, completed (and on time), still open, overdue, waived (left out of
 * the rates), cancelled (counted apart), completion records dated in the
 * range, and the two rates (floored whole percentages; a dash over nothing).
 * With more than one site, each course row can show its per-site rows.
 *
 * The route REQUIRES from and to as YYYY-MM-DD (18.23): both are sent every
 * time, and the form refuses a missing, impossible or reversed range before
 * asking. The default range is the last 90 days.
 */
import { Fragment, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import { fieldCls, primaryBtnCls } from '../../../../ui/styles';
import { useAuth } from '../../../../contexts/AuthContext';
import { getCompletionRates, isDayString, toDayString } from '../../../api/reports';
import type { RateCounts } from '../../../api/reports';
import {
  checkboxCls,
  ErrorNote,
  labelCls,
  metaCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LoadFailed, Loading, useAsync } from '../matrix/shared';

/** The default range: the last 90 days, today included. */
export const DEFAULT_RANGE_DAYS = 90;

export function defaultRange(now: Date = new Date()): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - DEFAULT_RANGE_DAYS);
  return { from: toDayString(from), to: toDayString(now) };
}

/** A rate as text: "83%", or a dash over nothing. */
export function rateText(pct: number | null | undefined): string {
  return typeof pct === 'number' ? `${pct}%` : '\u2014';
}

interface Applied {
  from: string;
  to: string;
  siteId: string;
}

/** The problem with a range, or null when it can be asked for. */
export function rangeProblem(from: string, to: string): string | null {
  if (!isDayString(from) || !isDayString(to)) return 'Choose both a start and an end date.';
  if (from > to) return 'The start date must be on or before the end date.';
  return null;
}

const COLUMNS: Array<{ key: keyof RateCounts; label: string }> = [
  { key: 'assigned', label: 'Assigned' },
  { key: 'completed', label: 'Completed' },
  { key: 'completed_on_time', label: 'On time' },
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'waived', label: 'Waived' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'completions', label: 'Completions' },
];

function CountCells({ counts }: { counts: RateCounts }) {
  return (
    <>
      {COLUMNS.map((c) => (
        <td key={c.key} className={`${tdCls} text-right tabular-nums`}>
          {Number(counts[c.key] ?? 0).toLocaleString()}
        </td>
      ))}
      <td className={`${tdCls} text-right tabular-nums`}>{rateText(counts.completion_rate_pct)}</td>
      <td className={`${tdCls} text-right tabular-nums`}>{rateText(counts.on_time_rate_pct)}</td>
    </>
  );
}

export function CompletionRatesPanel() {
  const { availableSites, currentSite } = useAuth();
  const initial = useMemo(() => defaultRange(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [siteId, setSiteId] = useState('');
  const [applied, setApplied] = useState<Applied>({ ...initial, siteId: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [bySite, setBySite] = useState(false);

  const rates = useAsync(
    () => getCompletionRates({ from: applied.from, to: applied.to, siteId: applied.siteId || null }),
    JSON.stringify(applied),
    'The completion rates could not be loaded.'
  );

  const networkSites = useMemo(
    () =>
      (availableSites ?? [])
        .filter((s) => s.is_active !== false && (!currentSite?.network_id || s.network_id === currentSite.network_id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableSites, currentSite]
  );

  const run = () => {
    const problem = rangeProblem(from, to);
    setFormError(problem);
    if (problem) return;
    setApplied({ from, to, siteId });
  };

  const data = rates.data;
  const siteName = new Map((data?.sites ?? []).map((s) => [s.id, s.name ?? 'Unnamed site']));
  const multiSite = (data?.sites.length ?? 0) > 1;

  return (
    <div className="space-y-4" data-testid="report-rates">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <div>
          <label className={labelCls} htmlFor="rates-from">
            From
          </label>
          <input id="rates-from" type="date" className={`${fieldCls} min-h-[44px] sm:min-h-0`} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rates-from" />
        </div>
        <div>
          <label className={labelCls} htmlFor="rates-to">
            To
          </label>
          <input id="rates-to" type="date" className={`${fieldCls} min-h-[44px] sm:min-h-0`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="rates-to" />
        </div>
        <div>
          <span className={labelCls}>Site</span>
          <FilterSelect label="Site" value={siteId} onChange={setSiteId} auto>
            <option value="">All my sites</option>
            {networkSites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </FilterSelect>
        </div>
        <button type="submit" className={primaryBtnCls} disabled={rates.loading} data-testid="rates-run">
          Show rates
        </button>
      </form>
      {formError ? <ErrorNote testId="rates-form-error">{formError}</ErrorNote> : null}

      {rates.error ? <LoadFailed message={rates.error} onRetry={rates.reload} testId="rates-error" /> : null}
      {!data && rates.loading ? <Loading rows={4} /> : null}

      {data ? (
        <p className={metaCls} data-testid="rates-summary">
          {`Assignments due from ${data.from} to ${data.to} (${data.timezone}), ${data.sites
            .map((s) => `${s.name ?? 'Unnamed site'}: ${s.people.toLocaleString()} people`)
            .join('; ')}.`}
        </p>
      ) : null}

      {data && data.rows.length === 0 ? (
        <EmptyState icon={BarChart3} variant="card" title="Nothing was due in this range" hint="Choose a wider range or another site." />
      ) : null}

      {data && data.rows.length > 0 ? (
        <>
          {multiSite ? (
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
              <input type="checkbox" className={checkboxCls} checked={bySite} onChange={(e) => setBySite(e.target.checked)} data-testid="rates-by-site" />
              Show each site
            </label>
          ) : null}
          <div className={tableCardCls}>
            <table className={tableCls} data-testid="rates-table">
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Course
                  </th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} scope="col" className={`${thCls} text-right`}>
                      {c.label}
                    </th>
                  ))}
                  <th scope="col" className={`${thCls} text-right`}>
                    Completion rate
                  </th>
                  <th scope="col" className={`${thCls} text-right`}>
                    On-time rate
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {data.rows.map((row) => (
                  <Fragment key={row.course.id}>
                    <tr data-testid={`rates-row-${row.course.id}`}>
                      <th scope="row" className={`${tdCls} text-left font-medium`}>
                        {row.course.title}
                        {!row.course.is_active ? <span className={`${metaCls} block`}>Retired</span> : null}
                      </th>
                      <CountCells counts={row} />
                    </tr>
                    {bySite && multiSite
                      ? row.by_site.map((s) => (
                          <tr key={`${row.course.id}:${s.site_id}`} className="bg-gray-50/60 dark:bg-gray-900/30" data-testid={`rates-site-${row.course.id}-${s.site_id}`}>
                            <th scope="row" className={`${tdCls} pl-8 text-left font-normal text-gray-600 dark:text-gray-300`}>
                              {siteName.get(s.site_id) ?? 'Site'}
                            </th>
                            <CountCells counts={s} />
                          </tr>
                        ))
                      : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className={metaCls}>
            Waived assignments are left out of both rates; cancelled ones are counted apart. Completions counts every completion record dated in the range.
          </p>
        </>
      ) : null}
    </div>
  );
}

export default CompletionRatesPanel;
